import { useState } from 'react';
import type { DepartmentsResponse, DirectoryResponse, ResponsibilitiesResponse, TeamsResponse } from '../../api/types';
import { apiPost, describeError } from '../../api/client';
import { useApiResource } from '../../hooks/useApiResource';
import { ErrorState, LoadingState } from '../StateMessage';
import { ActionFeedback, AdminForm, Field, useAction } from './adminShared';
import styles from './admin.module.css';

/**
 * Organisational Responsibility — the admin surface for
 * `api/src/routes/organisationalResponsibility.ts` (Mission 162), exposing
 * a domain/authorization layer that already existed, fully built and
 * audited (`docs/DOMAIN_DECISIONS.md` D-1 to D-9), with no UI above it
 * until now.
 *
 * ---------------------------------------------------------------------
 * WHAT A RESPONSIBILITY IS, SHOWN DELIBERATELY PLAINLY
 *
 * An assigned organisational scope — a department, a team, or another
 * employee — held by one employee. It is distinct from job title (D-1,
 * D-4): this section never asks for or shows a title, only a target. It is
 * distinct from Manager (D-6): assigning responsibility here changes
 * nothing about who may assess whom — that remains exactly
 * `isDirectManagerOf`, unaffected by anything on this page. This section
 * confers visibility only, and says so.
 *
 * A separate section from Employee Placement, deliberately: placement is
 * "where does this person sit," responsibility is "what can this person
 * see" — different questions, kept visibly distinct rather than folded
 * into one combined "manage this employee" panel.
 *
 * Organisation-wide responsibility is not offered by this form (department,
 * team and employee targets only) — there is exactly one organisation, so
 * assigning responsibility for the whole of it is a rare, high-impact
 * action better made through a deliberate follow-up than a dropdown option
 * offered alongside routine department/team assignments. The API itself
 * supports it; this UI's own scope trim does not remove that capability.
 * ---------------------------------------------------------------------
 */
export function ResponsibilitySection() {
  const directory = useApiResource<DirectoryResponse>('/employees');
  const departments = useApiResource<DepartmentsResponse>('/admin/departments');
  const teams = useApiResource<TeamsResponse>('/admin/teams');
  const [employeeId, setEmployeeId] = useState<string | null>(null);

  const loading = directory.status === 'loading' || departments.status === 'loading' || teams.status === 'loading';
  if (loading) return <LoadingState label="Loading organisational responsibility…" />;
  if (directory.status === 'failed') {
    return <ErrorState title="The employee list could not be loaded" detail={describeError(directory.error)} />;
  }
  if (departments.status === 'failed') {
    return <ErrorState title="The department list could not be loaded" detail={describeError(departments.error)} />;
  }
  if (teams.status === 'failed') {
    return <ErrorState title="The team list could not be loaded" detail={describeError(teams.error)} />;
  }

  const employees = directory.data.employees;
  const activeDepartments = departments.data.departments.filter((d) => d.status === 'active');
  const activeTeams = teams.data.teams.filter((t) => t.status === 'active');
  const selectedEmployeeId = employeeId ?? employees[0]?.id ?? null;

  return (
    <section className={styles.section} aria-label="Organisational responsibility">
      <h2 className={styles.sectionTitle}>Organisational responsibility</h2>
      <p className={styles.sectionNote}>
        An assigned scope — a department, a team, or another employee — that lets the holder see people they would not
        otherwise be able to. It is not a job title, and it never grants the ability to assess anyone.
      </p>

      {employees.length === 0 ? (
        <p className={styles.rowMeta}>No employees exist yet.</p>
      ) : (
        <>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="responsibility-holder">
              Holder
            </label>
            <select
              id="responsibility-holder"
              className={styles.input}
              value={selectedEmployeeId ?? ''}
              onChange={(event) => setEmployeeId(event.target.value)}
            >
              {employees.map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.fullName}
                </option>
              ))}
            </select>
          </div>

          {selectedEmployeeId ? (
            <ResponsibilityDetail
              employeeId={selectedEmployeeId}
              employees={employees}
              departments={activeDepartments}
              teams={activeTeams}
            />
          ) : null}
        </>
      )}
    </section>
  );
}

function targetLabel(
  responsibility: ResponsibilitiesResponse['responsibilities'][number],
  employees: DirectoryResponse['employees'],
  departments: DepartmentsResponse['departments'],
  teams: TeamsResponse['teams']
): string {
  switch (responsibility.targetType) {
    case 'organisation':
      return 'The whole organisation';
    case 'department':
      return departments.find((d) => d.id === responsibility.targetId)?.name ?? 'A department no longer listed here';
    case 'team':
      return teams.find((t) => t.id === responsibility.targetId)?.name ?? 'A team no longer listed here';
    case 'employee':
      return employees.find((e) => e.id === responsibility.targetId)?.fullName ?? 'An employee no longer listed here';
  }
}

function ResponsibilityDetail({
  employeeId,
  employees,
  departments,
  teams,
}: {
  employeeId: string;
  employees: DirectoryResponse['employees'];
  departments: DepartmentsResponse['departments'];
  teams: TeamsResponse['teams'];
}) {
  const responsibilities = useApiResource<ResponsibilitiesResponse>(`/admin/employees/${employeeId}/responsibilities`);
  const [assigning, setAssigning] = useState(false);
  const [ending, setEnding] = useState<string | null>(null);

  if (responsibilities.status === 'loading') return <LoadingState label="Loading current responsibilities…" />;
  if (responsibilities.status === 'failed') {
    return <ErrorState title="This employee's responsibilities could not be loaded" detail={describeError(responsibilities.error)} />;
  }

  const current = responsibilities.data.responsibilities.filter((r) => r.effectiveTo === null);

  function reload() {
    responsibilities.reload();
  }

  return (
    <div className={styles.row}>
      {current.length === 0 ? (
        <p className={styles.rowMeta}>No current responsibility is held.</p>
      ) : (
        <ul className={styles.rowMeta}>
          {current.map((responsibility) => (
            <li key={responsibility.id}>
              {targetLabel(responsibility, employees, departments, teams)} (since {responsibility.effectiveFrom})
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={() => setEnding((open) => (open === responsibility.id ? null : responsibility.id))}
              >
                {ending === responsibility.id ? 'Close' : 'End'}
              </button>
              {ending === responsibility.id ? (
                <EndResponsibilityForm
                  responsibilityId={responsibility.id}
                  onDone={() => {
                    setEnding(null);
                    reload();
                  }}
                />
              ) : null}
            </li>
          ))}
        </ul>
      )}

      <button type="button" className={styles.secondaryButton} onClick={() => setAssigning((open) => !open)}>
        {assigning ? 'Close' : 'Assign responsibility'}
      </button>

      {assigning ? (
        <AssignResponsibilityForm
          employeeId={employeeId}
          employees={employees.filter((e) => e.id !== employeeId)}
          departments={departments}
          teams={teams}
          onDone={() => {
            setAssigning(false);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

type TargetType = 'department' | 'team' | 'employee';

function AssignResponsibilityForm({
  employeeId,
  employees,
  departments,
  teams,
  onDone,
}: {
  employeeId: string;
  employees: DirectoryResponse['employees'];
  departments: DepartmentsResponse['departments'];
  teams: TeamsResponse['teams'];
  onDone: () => void;
}) {
  const [targetType, setTargetType] = useState<TargetType>('department');
  const optionsFor = (type: TargetType) =>
    type === 'department' ? departments : type === 'team' ? teams : employees;
  const [targetId, setTargetId] = useState(optionsFor('department')[0]?.id ?? '');
  const [effectiveFrom, setEffectiveFrom] = useState(todayIso());
  const { state, run } = useAction();

  function changeType(next: TargetType) {
    setTargetType(next);
    setTargetId(optionsFor(next)[0]?.id ?? '');
  }

  const options = optionsFor(targetType);

  async function submit() {
    if (!targetId) return;
    const ok = await run(async () => {
      await apiPost(`/admin/employees/${employeeId}/responsibilities`, { targetType, targetId, effectiveFrom });
      return 'The responsibility was assigned.';
    });
    if (ok) onDone();
  }

  return (
    <AdminForm title="Assign responsibility" onSubmit={submit} submitLabel="Assign" busy={state.status === 'saving'}>
      <div className={styles.field}>
        <label className={styles.label} htmlFor={`responsibility-target-type-${employeeId}`}>
          Scope
        </label>
        <select
          id={`responsibility-target-type-${employeeId}`}
          className={styles.input}
          value={targetType}
          onChange={(event) => changeType(event.target.value as TargetType)}
        >
          <option value="department">A department</option>
          <option value="team">A team</option>
          <option value="employee">Another employee</option>
        </select>
      </div>

      {options.length === 0 ? (
        <p className={styles.hint}>Nothing is available to select for this scope.</p>
      ) : (
        <div className={styles.field}>
          <label className={styles.label} htmlFor={`responsibility-target-${employeeId}`}>
            {targetType === 'department' ? 'Department' : targetType === 'team' ? 'Team' : 'Employee'}
          </label>
          <select
            id={`responsibility-target-${employeeId}`}
            className={styles.input}
            value={targetId}
            onChange={(event) => setTargetId(event.target.value)}
          >
            {options.map((option) => (
              <option key={option.id} value={option.id}>
                {'name' in option ? option.name : option.fullName}
              </option>
            ))}
          </select>
        </div>
      )}

      <Field
        id={`responsibility-from-${employeeId}`}
        label="Effective from (YYYY-MM-DD)"
        value={effectiveFrom}
        onChange={setEffectiveFrom}
      />
      <ActionFeedback state={state} />
    </AdminForm>
  );
}

function EndResponsibilityForm({ responsibilityId, onDone }: { responsibilityId: string; onDone: () => void }) {
  const [effectiveTo, setEffectiveTo] = useState(todayIso());
  const { state, run } = useAction();

  async function submit() {
    const ok = await run(async () => {
      await apiPost(`/admin/responsibilities/${responsibilityId}/ending`, { effectiveTo });
      return 'The responsibility was ended.';
    });
    if (ok) onDone();
  }

  return (
    <AdminForm title="End responsibility" onSubmit={submit} submitLabel="End" busy={state.status === 'saving'}>
      <Field
        id={`responsibility-ending-${responsibilityId}`}
        label="Effective to (YYYY-MM-DD)"
        value={effectiveTo}
        onChange={setEffectiveTo}
        hint="The responsibility stays on record; it stops being current after this date."
      />
      <ActionFeedback state={state} />
    </AdminForm>
  );
}
